import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { PasskeyRecord } from '../crypto/passkey.ts';
import type { LocalNote, LocalVersion, OutboxEntry, StoredAuth } from './types.ts';
import type { VaultIndexContent } from '../../../shared/protocol.ts';

/**
 * Lokaler Zwischenspeicher. Er hält den entschlüsselten Vault, damit die App offline
 * sofort startet und die Suche ohne Netz funktioniert. Beim Abmelden wird er
 * vollständig gelöscht (siehe wipe()).
 */
interface AtheneSchema extends DBSchema {
  meta: {
    key: string;
    value: unknown;
  };
  notes: {
    key: string;
    value: LocalNote;
    indexes: { updatedAt: number };
  };
  versions: {
    key: string;
    value: LocalVersion;
    indexes: { noteId: string };
  };
  outbox: {
    key: string;
    value: OutboxEntry;
  };
}

const DB_NAME = 'athene';
let handle: Promise<IDBPDatabase<AtheneSchema>> | null = null;

export function db(): Promise<IDBPDatabase<AtheneSchema>> {
  handle ??= openDB<AtheneSchema>(DB_NAME, 1, {
    upgrade(database) {
      database.createObjectStore('meta');
      const notes = database.createObjectStore('notes', { keyPath: 'id' });
      notes.createIndex('updatedAt', 'updatedAt');
      const versions = database.createObjectStore('versions', { keyPath: 'id' });
      versions.createIndex('noteId', 'noteId');
      database.createObjectStore('outbox', { keyPath: 'ref' });
    },
  });
  return handle;
}

async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await (await db()).get('meta', key)) as T | undefined;
}

async function setMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put('meta', value, key);
}

export const store = {
  getAuth: () => getMeta<StoredAuth>('auth'),

  /** Passkey-Eintrag dieses Geräts. Enthält den verpackten Vault Key. */
  getPasskey: () => getMeta<PasskeyRecord>('passkey'),
  setPasskey: (record: PasskeyRecord) => setMeta('passkey', record),
  clearPasskey: async () => {
    await (await db()).delete('meta', 'passkey');
  },

  setAuth: (auth: StoredAuth) => setMeta('auth', auth),

  /**
   * Schlüssel für die Titel in Mitteilungen. Liegt dauerhaft im Gerät, nicht nur im
   * Arbeitsspeicher — der Service Worker braucht ihn bei gesperrtem Vault.
   */
  getNotifyKey: () => getMeta<Uint8Array>('notifyKey'),
  setNotifyKey: (key: Uint8Array) => setMeta('notifyKey', key),

  getLastPull: async () => (await getMeta<number>('lastPull')) ?? 0,
  setLastPull: (value: number) => setMeta('lastPull', value),

  /** Wie Bilder vor dem Hochladen behandelt werden. */
  getImageMode: async () => (await getMeta<'small' | 'balanced' | 'original'>('imageMode')) ?? 'balanced',
  setImageMode: (value: 'small' | 'balanced' | 'original') => setMeta('imageMode', value),

  /** Minuten bis zur automatischen Sperre; 0 heißt nie. */
  getLockAfter: async () => (await getMeta<number>('lockAfter')) ?? 30,
  setLockAfter: (value: number) => setMeta('lockAfter', value),

  getDensity: async () => (await getMeta<'compact' | 'airy'>('density')) ?? null,
  setDensity: (value: 'compact' | 'airy') => setMeta('density', value),

  /** Nutzerweiter Cursor im Update-Log des Servers. */
  getLastSeq: async () => (await getMeta<number>('lastSeq')) ?? 0,
  setLastSeq: (value: number) => setMeta('lastSeq', value),

  async getDeviceId(): Promise<string> {
    const existing = await getMeta<string>('deviceId');
    if (existing) return existing;
    const created = crypto.randomUUID();
    await setMeta('deviceId', created);
    return created;
  },

  getIndex: async () =>
    (await getMeta<VaultIndexContent>('index')) ?? { folders: [], lastExportAt: null },
  setIndex: (value: VaultIndexContent) => setMeta('index', value),

  async allNotes(): Promise<LocalNote[]> {
    return (await db()).getAll('notes');
  },
  async getNote(id: string): Promise<LocalNote | undefined> {
    return (await db()).get('notes', id);
  },
  async putNote(note: LocalNote): Promise<void> {
    await (await db()).put('notes', note);
  },
  /**
   * Teiländerung in einer einzigen Transaktion. Notwendig, weil Persistieren des
   * Textes und Fortschreiben der Sequenznummern nebenläufig passieren — ein
   * Lesen-Ändern-Schreiben über zwei Transaktionen würde den jeweils anderen Stand
   * überschreiben.
   */
  async patchNote(
    id: string,
    change: (note: LocalNote) => LocalNote,
  ): Promise<LocalNote | undefined> {
    const tx = (await db()).transaction('notes', 'readwrite');
    const existing = await tx.store.get(id);
    if (!existing) {
      await tx.done;
      return undefined;
    }
    const next = change(existing);
    await tx.store.put(next);
    await tx.done;
    return next;
  },
  async removeNote(id: string): Promise<void> {
    await (await db()).delete('notes', id);
  },
  async dirtyNotes(): Promise<LocalNote[]> {
    return (await this.allNotes()).filter((n) => n.dirty);
  },

  async versionsOf(noteId: string): Promise<LocalVersion[]> {
    const all = await (await db()).getAllFromIndex('versions', 'noteId', noteId);
    return all.sort((a, b) => b.createdAt - a.createdAt);
  },
  async putVersion(version: LocalVersion): Promise<void> {
    await (await db()).put('versions', version);
  },
  async removeVersions(ids: string[]): Promise<void> {
    const tx = (await db()).transaction('versions', 'readwrite');
    await Promise.all(ids.map((id) => tx.store.delete(id)));
    await tx.done;
  },
  async unpushedVersions(): Promise<LocalVersion[]> {
    return (await (await db()).getAll('versions')).filter((v) => !v.pushed);
  },

  async queueUpdate(entry: OutboxEntry): Promise<void> {
    await (await db()).put('outbox', entry);
  },
  async outbox(): Promise<OutboxEntry[]> {
    return (await (await db()).getAll('outbox')).sort((a, b) => a.createdAt - b.createdAt);
  },
  async dequeue(ref: string): Promise<void> {
    await (await db()).delete('outbox', ref);
  },
};

/** Vollständiges Löschen des lokalen Klartext-Zwischenspeichers. */
export async function wipe(): Promise<void> {
  const database = await db();
  database.close();
  handle = null;
  await new Promise<void>((resolve) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}
